import React, { useState } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  Container,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Snackbar,
  Alert,
  useMediaQuery,
} from "@mui/material";

const steps = [
  "Meter Number",
  "Payment Method",
  "Phone Number",
  "Amount",
  "Confirmation",
];

// Service fee calculation (same as PHP logic)
function calculateServiceFee(lastChargeDate) {
  if (!lastChargeDate) return 3000; // never charged before

  const last = new Date(lastChargeDate);
  const now = new Date();
  const diffMs = now - last;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 30) return 0;

  const periods = Math.floor(diffDays / 30);
  return periods * 3000;
}

function StepContent({ step, formData, setFormData }) {
  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  switch (step) {
    case 0:
      return (
        <TextField
          label="Enter Meter Number"
          value={formData.meter}
          onChange={handleChange("meter")}
          fullWidth
          sx={{ mt: 2 }}
        />
      );

    case 1:
      return (
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            Choose Payment Method
          </Typography>
          <RadioGroup
            row
            value={formData.sourceSystem}
            onChange={handleChange("sourceSystem")}
            sx={{ justifyContent: "center" }}
          >
            <FormControlLabel value="MTN" control={<Radio />} label="MTN" />
            <FormControlLabel
              value="AIRTEL"
              control={<Radio />}
              label="AIRTEL"
            />
            <FormControlLabel
              value="FLEXIPAY"
              control={<Radio />}
              label="FLEXIPAY"
            />
          </RadioGroup>
        </Box>
      );

    case 2:
      return (
        <TextField
          label="Phone Number (start with 256)"
          value={formData.phone}
          onChange={handleChange("phone")}
          fullWidth
          sx={{ mt: 2 }}
        />
      );

    case 3:
      return (
        <TextField
          label="Amount"
          type="number"
          value={formData.amount}
          onChange={handleChange("amount")}
          fullWidth
          sx={{ mt: 2 }}
        />
      );

    case 4:
      return (
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Confirm Your Details
          </Typography>
          <Typography sx={{ mt: 1 }}>
            <strong>Customer Name:</strong> {formData.customerName}
          </Typography>
          <Typography sx={{ mt: 1 }}>
            <strong>Meter:</strong> {formData.meter}
          </Typography>
          <Typography sx={{ mt: 1 }}>
            <strong>Last Charge Date:</strong>{" "}
            {formData.lastChargeDate || "Never"}
          </Typography>
          <Typography sx={{ mt: 1 }}>
            <strong>Service Fee:</strong> UGX {formData.serviceFee}
          </Typography>
          <Typography sx={{ mt: 1 }}>
            <strong>Payment Method:</strong> {formData.sourceSystem}
          </Typography>
          <Typography sx={{ mt: 1 }}>
            <strong>Phone:</strong> {formData.phone}
          </Typography>
          <Typography sx={{ mt: 1 }}>
            <strong>Amount:</strong> UGX {formData.amount}
          </Typography>
          <Typography sx={{ mt: 1 }}>
            <strong>Total Payable:</strong> UGX {formData.totalAmountPayable}
          </Typography>
        </Box>
      );

    default:
      return null;
  }
}

export default function App() {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    meter: "",
    phone: "",
    amount: "",
    sourceSystem: "MTN",
    customerName: "",
    lastChargeDate: null,
    serviceFee: 0,
    totalAmountPayable: 0,
    account_id: null,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  // Step navigation
  const handleBack = () => setActiveStep((prev) => prev - 1);
  const handleReset = () => {
    setFormData({
      meter: "",
      phone: "",
      amount: "",
      sourceSystem: "MTN",
      customerName: "",
      lastChargeDate: null,
      serviceFee: 0,
      totalAmountPayable: 0,
      account_id: null,
    });
    setActiveStep(0);
  };

  const handleNext = async () => {
    // Step 0 → Verify meter number
    if (activeStep === 0) {
      if (!formData.meter) {
        setSnackbar({
          open: true,
          message: "Please enter a meter number",
          severity: "warning",
        });
        return;
      }

      try {
        const res = await fetch(
          `https://server.beamwater.co/api/verifyMeterNumber.php?meter_number=${formData.meter}`
        );
        const data = await res.json();

        if (data.status !== "success") {
          setSnackbar({
            open: true,
            message: data.message || "Invalid meter number",
            severity: "error",
          });
          return;
        }

        const { name, last_charge_date, phone_number, amount, account_id } =
          data.data;
        const fee = calculateServiceFee(last_charge_date);

        setFormData({
          ...formData,
          customerName: name,
          lastChargeDate: last_charge_date,
          phone: phone_number,
          amount: amount,
          serviceFee: fee,
          account_id: account_id,
        });

        setSnackbar({
          open: true,
          message: "Meter verified successfully",
          severity: "success",
        });
        setActiveStep((prev) => prev + 1);
        return;
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Network error verifying meter",
          severity: "error",
        });
        console.error(error);
        return;
      }
    }

    // Step 3 → compute total payable
    if (activeStep === 3) {
      const total = Number(formData.amount) + Number(formData.serviceFee);
      setFormData({ ...formData, totalAmountPayable: total });
    }

    setActiveStep((prev) => prev + 1);
  };

  const handlePayment = async () => {
    try {
      const response = await fetch(
        "https://server.beamwater.co/api/payment.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            meter_number: formData.meter,
            phone: formData.phone,
            amount: formData.amount,
            service_fee: formData.serviceFee,
            total_amount: formData.totalAmountPayable,
            last_charge_date: formData.lastChargeDate,
            sourceSystem: formData.sourceSystem,
            account_id: formData.account_id,
          }),
        }
      );

      const result = await response.json();
      const success = result.statusCode === "00";

      setSnackbar({
        open: true,
        message:
          result.statusDescription ||
          result.error ||
          (success ? "Payment Successful" : "Payment Failed"),
        severity: success ? "success" : "error",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Network or server error",
        severity: "error",
      });
      console.error(err);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 5, px: isSmallScreen ? 2 : 0 }}>
      <Paper
        sx={{
          p: isSmallScreen ? 2 : 4,
          borderRadius: 3,
          boxShadow: "0px 5px 20px rgba(0,0,0,0.1)",
          background: "linear-gradient(to bottom right, #ffffff, #f3f7ff)",
        }}
      >
        <Typography
          variant={isSmallScreen ? "h6" : "h5"}
          align="center"
          fontWeight="bold"
          sx={{ mb: 2, color: "#1a237e" }}
        >
          Beam Water Token Purchase
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Stepper
          activeStep={activeStep}
          alternativeLabel={!isSmallScreen}
          orientation={isSmallScreen ? "vertical" : "horizontal"}
          sx={{ mb: 3 }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel sx={{ fontWeight: "bold", wordBreak: "break-word" }}>
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ mt: 3, mb: 2 }}>
          <StepContent
            step={activeStep}
            formData={formData}
            setFormData={setFormData}
          />

          {activeStep === steps.length - 1 ? (
            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isSmallScreen ? "column" : "row",
                  justifyContent: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Button onClick={handleBack} fullWidth={isSmallScreen}>
                  Back
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  fullWidth={isSmallScreen}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handlePayment}
                  fullWidth={isSmallScreen}
                >
                  Pay Now
                </Button>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: isSmallScreen ? "column" : "row",
                justifyContent: "space-between",
                mt: 3,
                gap: isSmallScreen ? 1 : 0,
              }}
            >
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                fullWidth={isSmallScreen}
              >
                Back
              </Button>
              <Button
                variant="contained"
                sx={{
                  px: 4,
                  backgroundColor: "#1a237e",
                  ":hover": { backgroundColor: "#3949ab" },
                }}
                onClick={handleNext}
                fullWidth={isSmallScreen}
              >
                {activeStep === steps.length - 2 ? "Confirm" : "Next"}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
